import React, { useState } from 'react';
import { EuEnvironmentalGoals, EuEnvironmentalGoalsEffect, PropertyKey, TargetNumberDto } from 'src/api/v2';
import { lowercaseFirstLetter } from './Integer';
import { useParams } from 'react-router-dom';
import useTargetNumbers from '../../hooks/useTargetNumbers';
import { newValueDto } from '../../hooks/useAchievedGoalsColumns';
import css from './EuEnviromentalGoals.module.scss';
import { useTranslation } from 'react-i18next';

type Props = {
  targetNumber: TargetNumberDto;
  objectKey: PropertyKey;
  editMode: boolean;
};

// Oppretter en lokal variant her, da v2 gir oss noen readonly verdier.
interface LocalEuEnvironmentalGoalsValueDto {
  goalSatisfactions:
    | { -readonly [key in keyof typeof EuEnvironmentalGoals]?: EuEnvironmentalGoalsEffect }
    | undefined;
  ingen: boolean;
}

interface ICheckBoxState {
  [key: string]: boolean;
}

const EuEnviromentalGoals = ({ targetNumber, objectKey, editMode }: Props) => {
  const { goalId } = useParams();
  const idArray = goalId?.split('|') || [];
  const { updateMutation } = useTargetNumbers(idArray[0] || '', idArray[1] || '');

  const { t: tSiva } = useTranslation('siva');

  const dataObject: LocalEuEnvironmentalGoalsValueDto = !targetNumber.valueDto?.euEnvironmentalGoals
    ? { ingen: false, goalSatisfactions: undefined }
    : { ...targetNumber.valueDto.euEnvironmentalGoals };

  const [checkBoxState, setCheckboxState] = useState<ICheckBoxState>(() => {
    const initialState = {};
    if (targetNumber.valueDto?.euEnvironmentalGoals?.ingen) initialState['none'] = true;
    else
      Object.entries(targetNumber.valueDto?.euEnvironmentalGoals?.goalSatisfactions || {}).forEach(
        ([key, value]) =>
          value === EuEnvironmentalGoalsEffect.Positive
            ? (initialState[key + '__Positive'] = true)
            : (initialState[key + '__Negative'] = true),
      );
    return initialState;
  });

  const optionsList = Object.values(EuEnvironmentalGoals).map((eu) => {
    return {
      value: eu,
      text: eu,
    };
  });

  const onChange = (e: React.FormEvent<HTMLInputElement>) => {
    const checkboxData = e.currentTarget.value.split('__');
    const newState = { ...checkBoxState };
    const newListvalue = { ...dataObject };
    if (e.currentTarget.value === 'none') {
      if (e.currentTarget.checked === true) {
        setCheckboxState({ none: true });
        newListvalue['ingen'] = true;
        newListvalue['goalSatisfactions'] = undefined;
      } else {
        setCheckboxState({ none: false });
        newListvalue['ingen'] = false;
      }
    } else if (checkboxData[1] === 'Positive') {
      if (!newListvalue.goalSatisfactions) {
        newListvalue.goalSatisfactions = {};
      }
      if (Object.hasOwn(newState, checkboxData[0] + '__Negative')) {
        newState[checkboxData[0] + '__Negative'] = false;
      }
      setCheckboxState({ ...newState, none: false, [e.currentTarget.value]: e.currentTarget.checked });
      newListvalue['ingen'] = false;
      newListvalue.goalSatisfactions[checkboxData[0] as EuEnvironmentalGoals] =
        EuEnvironmentalGoalsEffect.Positive;
    } else {
      if (!newListvalue.goalSatisfactions) {
        newListvalue.goalSatisfactions = {};
      }
      if (Object.hasOwn(newState, checkboxData[0] + '__Positive')) {
        newState[checkboxData[0] + '__Positive'] = false;
      }
      setCheckboxState({ ...newState, none: false, [e.currentTarget.value]: e.currentTarget.checked });
      newListvalue['ingen'] = false;
      newListvalue.goalSatisfactions[checkboxData[0] as EuEnvironmentalGoals] =
        EuEnvironmentalGoalsEffect.Negative;
    }

    const newDto = { ...newValueDto };
    newDto[lowercaseFirstLetter(objectKey)] = newListvalue;
    updateMutation.mutateAsync([
      {
        ...targetNumber,
        valueDto: newDto,
      },
    ]);
  };

  const positiveEffect =
    dataObject.goalSatisfactions &&
    Object.entries(dataObject.goalSatisfactions).filter(
      ([_key, value]) => value === EuEnvironmentalGoalsEffect.Positive,
    );

  const negativeEffect =
    dataObject.goalSatisfactions &&
    Object.entries(dataObject.goalSatisfactions).filter(
      ([_key, value]) => value === EuEnvironmentalGoalsEffect.Negative,
    );

  return editMode ? (
    <div className="display--flex gap--2">
      <div className={css['eu-grid']}>
        <div></div>
        {optionsList.map((option) => (
          <div className="margin--auto" key={option.value + '__name'}>
            {option.text}
          </div>
        ))}
        <div>{tSiva('report.goals.eu.positive')}</div>
        {optionsList.map((option) => (
          <input
            key={option.value + '__Positive'}
            onChange={onChange}
            value={option.value + '__Positive'}
            type="checkbox"
            id={targetNumber.sivaContractId.toString() + option.value + '__Positive'}
            checked={checkBoxState[option.value + '__Positive'] === true}
          />
        ))}
        <div>{tSiva('report.goals.eu.negative')}</div>
        {optionsList.map((option) => (
          <input
            key={option.value + '__Negative'}
            onChange={onChange}
            value={option.value + '__Negative'}
            type="checkbox"
            id={targetNumber.sivaContractId.toString() + option.value + '__Negative'}
            checked={checkBoxState[option.value + '__Negative'] === true}
          />
        ))}
      </div>
      <div className="border-left padding-x--3">
        <label className="display--block" htmlFor={targetNumber.sivaContractId.toString() + 'none'}>
          {tSiva('report.goals.eu.none')}
        </label>
        <input
          className={css['none-cell']}
          onChange={onChange}
          value={'none'}
          type="checkbox"
          id={targetNumber.sivaContractId.toString() + 'none'}
          checked={checkBoxState['none']}
        />
      </div>
    </div>
  ) : (
    <>
      {dataObject.ingen === true ? (
        'Ingen'
      ) : (
        <div className="display--flex gap--4">
          {positiveEffect && positiveEffect.length > 0 && (
            <div>
              <span className="font-weight--bold">Vesentlig positiv: </span>
              {positiveEffect
                .map(([key]) => key)
                .sort()
                .map((value, index, array) => `${value}${index !== array.length - 1 ? ', ' : ''}`)}
            </div>
          )}
          {negativeEffect && negativeEffect.length > 0 && (
            <div>
              <span className="font-weight--bold">Vesentlig negativ: </span>
              {negativeEffect
                .map(([key]) => key)
                .sort()
                .map((value, index, array) => `${value}${index !== array.length - 1 ? ', ' : ''}`)}
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default EuEnviromentalGoals;
